/**
 * Bank Entity Service - Proposal Management
 *
 * Updated to use the new unified API client for consistent mock/real API switching
 */

import { apiClient } from '@/services/api/client';
import type {
  BankProposalQueue,
  LoanProposalWithDetails,
  RiskAssessment,
  PortfolioSummary,
  FinancialMetrics,
} from '@/types/api/lending';

// Legacy interfaces for backwards compatibility
import {
  ProposalReviewState,
  BankProposalResponse,
  DecisionType,
  ProposalPriority,
  ReviewStage,
} from '@/types/entities/bank';

// ====== BANK PROPOSAL INTERFACES (Legacy Support) ======

export interface BankProposalFilters {
  status?: ReviewStage[];
  priority?: ProposalPriority[];
  amountRange?: {
    min: number;
    max: number;
  };
  submittedDateRange?: {
    start: string;
    end: string;
  };
  assignedReviewer?: string;
  communityId?: string;
}

export interface ProposalDecisionData {
  proposalId: string;
  decision: DecisionType;
  approvedAmount?: number;
  interestRate?: number;
  termMonths?: number;
  conditions?: string[];
  rejectionReasons?: string[];
  internalNotes?: string;
}

export interface ProposalAssignmentData {
  proposalId: string;
  reviewerId: string;
  priority?: ProposalPriority;
  targetCompletionDate?: string;
  notes?: string;
}

// ====== MODERNIZED BANK PROPOSAL SERVICE ======

class BankProposalService {
  /**
   * Get bank's proposal queue with modern API client
   */
  async getProposalQueue(
    bankId: string,
    filters?: BankProposalFilters,
  ): Promise<BankProposalQueue> {
    try {
      const response = await apiClient.bank.getProposalQueue(bankId, {
        bank_id: bankId,
        page: 1,
        limit: 50,
        // Note: Filters will be mapped when real API supports them
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching proposal queue:', error);
      throw error;
    }
  }

  /**
   * Get specific proposal by ID
   */
  async getProposalById(id: string): Promise<LoanProposalWithDetails> {
    try {
      const response = await apiClient.bank.getProposal(id);
      return response.data;
    } catch (error) {
      console.error('Error fetching proposal:', error);
      throw error;
    }
  }

  /**
   * Get risk assessment for proposal
   */
  async getRiskAssessment(proposalId: string): Promise<RiskAssessment> {
    try {
      const response = await apiClient.bank.getRiskAssessment(proposalId);
      return response.data;
    } catch (error) {
      console.error('Error fetching risk assessment:', error);
      throw error;
    }
  }

  /**
   * Get portfolio summary
   */
  async getPortfolioSummary(bankId: string): Promise<PortfolioSummary> {
    try {
      const response = await apiClient.bank.getPortfolioSummary(bankId);
      return response.data;
    } catch (error) {
      console.error('Error fetching portfolio summary:', error);
      throw error;
    }
  }

  /**
   * Get financial metrics
   */
  async getFinancialMetrics(bankId: string): Promise<FinancialMetrics> {
    try {
      const response = await apiClient.financial.getMetrics(bankId);
      return response.data;
    } catch (error) {
      console.error('Error fetching financial metrics:', error);
      throw error;
    }
  }

  /**
   * Submit decision for proposal (legacy compatibility)
   */
  async submitDecision(data: ProposalDecisionData): Promise<BankProposalResponse> {
    try {
      // For now, return mock response until real API is implemented
      return this.getMockDecisionResponse(data);
    } catch (error) {
      console.error('Error submitting decision:', error);
      throw error;
    }
  }

  /**
   * Get proposal review state (legacy compatibility)
   */
  async getProposalReviewState(proposalId: string): Promise<ProposalReviewState> {
    try {
      // For now, return mock data until real API supports this
      return this.getMockReviewState(proposalId);
    } catch (error) {
      console.error('Error fetching review state:', error);
      return this.getMockReviewState(proposalId);
    }
  }

  /**
   * Get environment info for debugging
   */
  getEnvironmentInfo() {
    return apiClient.getEnvironmentInfo();
  }

  // ====== ADDITIONAL METHODS FOR BACKWARDS COMPATIBILITY ======

  /**
   * Assign proposal to reviewer (legacy compatibility)
   */
  async assignProposal(data: ProposalAssignmentData): Promise<void> {
    try {
      // For now, simulate assignment until real API is implemented
      console.log('Mock: Proposal assigned successfully', data);
      return;
    } catch (error) {
      console.error('Error assigning proposal:', error);
      throw error;
    }
  }

  /**
   * Update proposal priority (legacy compatibility)
   */
  async updateProposalPriority(proposalId: string, priority: ProposalPriority): Promise<void> {
    try {
      console.log('Mock: Priority updated successfully', { proposalId, priority });
      return;
    } catch (error) {
      console.error('Error updating proposal priority:', error);
      throw error;
    }
  }

  /**
   * Advance proposal to next stage (legacy compatibility)
   */
  async advanceToNextStage(
    proposalId: string,
    currentStage: ReviewStage,
    notes?: string,
  ): Promise<void> {
    try {
      console.log('Mock: Stage advanced successfully', { proposalId, currentStage, notes });
      return;
    } catch (error) {
      console.error('Error advancing proposal stage:', error);
      throw error;
    }
  }

  /**
   * Add reviewer notes (legacy compatibility)
   */
  async addReviewerNotes(
    proposalId: string,
    notes: string,
    stage: ReviewStage,
    isInternal: boolean = true,
  ): Promise<void> {
    try {
      console.log('Mock: Notes added successfully', { proposalId, notes, stage, isInternal });
      return;
    } catch (error) {
      console.error('Error adding reviewer notes:', error);
      throw error;
    }
  }

  /**
   * Request additional information for proposal (legacy compatibility)
   */
  async requestAdditionalInfo(
    proposalId: string,
    requestedInfo: string[],
    message?: string,
  ): Promise<void> {
    try {
      console.log('Mock: Additional info requested successfully', {
        proposalId,
        requestedInfo,
        message,
      });
      return;
    } catch (error) {
      console.error('Error requesting additional info:', error);
      throw error;
    }
  }

  /**
   * Bulk assign proposals (legacy compatibility)
   */
  async bulkAssignProposals(proposalIds: string[], reviewerId: string): Promise<void> {
    try {
      console.log('Mock: Bulk assignment completed successfully', { proposalIds, reviewerId });
      return;
    } catch (error) {
      console.error('Error bulk assigning proposals:', error);
      throw error;
    }
  }

  /**
   * Bulk update priority (legacy compatibility)
   */
  async bulkUpdatePriority(proposalIds: string[], priority: ProposalPriority): Promise<void> {
    try {
      console.log('Mock: Bulk priority update completed successfully', { proposalIds, priority });
      return;
    } catch (error) {
      console.error('Error bulk updating priority:', error);
      throw error;
    }
  }

  // ====== LEGACY MOCK DATA FOR BACKWARDS COMPATIBILITY ======

  private getMockDecisionResponse(data: ProposalDecisionData): BankProposalResponse {
    const now = new Date();
    return {
      proposalId: data.proposalId,
      bankId: 'bank-001',
      responseDate: now,
      decision: data.decision,
      approvedAmount: data.approvedAmount,
      interestRate: data.interestRate,
      termMonths: data.termMonths,
      processingTimeline: {
        estimated: [],
        actual: [],
      },
      nextSteps:
        data.decision === 'approved'
          ? ['Legal document preparation', 'Loan agreement signing', 'Funds disbursement']
          : ['Review feedback provided', 'Address concerns', 'Resubmit application'],
      bankContactInfo: {
        primaryContact: {
          name: 'Sarah Johnson',
          title: 'Senior Loan Officer',
          email: 'sarah.johnson@bank.com',
          phone: '555-0100',
          availability: 'Monday-Friday 9AM-5PM',
        },
        lendingOfficer: {
          name: 'Sarah Johnson',
          title: 'Senior Loan Officer',
          email: 'sarah.johnson@bank.com',
          phone: '555-0100',
          availability: 'Monday-Friday 9AM-5PM',
        },
        loanProcessor: {
          name: 'Mike Chen',
          title: 'Loan Processor',
          email: 'mike.chen@bank.com',
          phone: '555-0101',
          availability: 'Monday-Friday 8AM-6PM',
        },
        customerService: {
          name: 'Customer Service',
          title: 'Support Team',
          email: 'support@bank.com',
          phone: '555-0199',
          availability: '24/7',
        },
      },
      individualApprovals: [],
      legalDocuments: [],
      complianceChecklist: [],
    };
  }

  private getMockReviewState(proposalId: string): ProposalReviewState {
    return {
      proposalId,
      currentStage: 'document_review',
      stages: [
        {
          stage: 'intake',
          status: 'completed',
          startedAt: new Date(Date.now() - 48 * 60 * 60 * 1000),
          completedAt: new Date(Date.now() - 46 * 60 * 60 * 1000),
          reviewerId: 'reviewer-001',
          reviewerName: 'Sarah Johnson',
          slaHours: 4,
          elapsedHours: 2,
        },
        {
          stage: 'document_review',
          status: 'in_progress',
          startedAt: new Date(Date.now() - 46 * 60 * 60 * 1000),
          reviewerId: 'reviewer-001',
          reviewerName: 'Sarah Johnson',
          slaHours: 24,
          elapsedHours: 46,
        },
      ],
      percentComplete: 15,
      blockingIssues: ['Missing vehicle title verification for Unit 45'],
      riskLevel: 'medium',
      estimatedDecisionDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    };
  }
}

export const bankProposalService = new BankProposalService();
